'use client'

import * as React from 'react'
import { Editor } from '@tiptap/react'
import { Trash2 } from 'lucide-react'
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@deepguide-ai/ui/popover'
import { Button } from '@deepguide-ai/ui/button'
import { deriveRunbookRefAlias } from '@/tiptap/tiptap-extensions/common/runbook-ref-mention/derive-alias'

interface RunbookRefMentionManagerProps {
  editor: Editor
  readOnly?: boolean
}

type RefScope = 'params' | 'step' | 'run' | 'env' | 'event'

function refScope(ref: string): RefScope | null {
  const match = /^@(params|step|run|env|event)\b/.exec(ref || '')
  if (!match) return null
  return match[1] as RefScope
}

function scopeLabel(scope: ReturnType<typeof refScope>): string {
  if (scope === 'params') return 'Procedure parameter'
  if (scope === 'step') return 'Step output'
  if (scope === 'run') return 'Run metadata'
  if (scope === 'env') return 'Environment value'
  // RFC-194 §8.7 — trigger system context.
  if (scope === 'event') return 'Event context'
  return 'Reference'
}

/**
 * Listens for `openRunbookRefMentionEditor` events emitted by the
 * RunbookRefMention node view and renders a small popover with the
 * canonical ref details and a delete affordance. Mirrors the
 * ActionPillManager pattern.
 */
export function RunbookRefMentionManager({
  editor,
  readOnly = false,
}: RunbookRefMentionManagerProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [ref, setRef] = React.useState('')
  const [nodePos, setNodePos] = React.useState<number | null>(null)
  const [anchorRect, setAnchorRect] = React.useState<DOMRect | null>(null)

  React.useEffect(() => {
    const handleOpen = ({ pos }: { pos?: number; ref?: string } = {}) => {
      if (pos == null) return
      const targetNode = editor.state.doc.nodeAt(pos)
      if (!targetNode || targetNode.type.name !== 'runbookRefMention') return

      const canonical =
        typeof targetNode.attrs.ref === 'string' ? targetNode.attrs.ref : ''

      setRef(canonical)
      setNodePos(pos)

      try {
        const domNode = editor.view.nodeDOM(pos)
        if (domNode instanceof HTMLElement) {
          setAnchorRect(domNode.getBoundingClientRect())
        } else {
          const coords = editor.view.coordsAtPos(pos)
          setAnchorRect(
            new DOMRect(coords.left, coords.top, 0, coords.bottom - coords.top)
          )
        }
      } catch {
        setAnchorRect(null)
      }

      setIsOpen(true)
    }

    editor.on('openRunbookRefMentionEditor' as any, handleOpen)
    return () => {
      editor.off('openRunbookRefMentionEditor' as any, handleOpen)
    }
  }, [editor])

  const handleDelete = () => {
    if (nodePos != null) {
      try {
        editor
          .chain()
          .focus()
          .setNodeSelection(nodePos)
          .deleteSelection()
          .run()
      } catch {
        // ignore — node may have been removed already
      }
    }
    setIsOpen(false)
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  const alias = deriveRunbookRefAlias(ref)
  const scope = refScope(ref)

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose()
      }}
    >
      <PopoverAnchor asChild>
        <span
          aria-hidden
          style={{
            position: 'fixed',
            left: anchorRect?.left ?? 0,
            top: anchorRect?.top ?? 0,
            width: anchorRect?.width ?? 0,
            height: anchorRect?.height ?? 0,
            pointerEvents: 'none',
          }}
        />
      </PopoverAnchor>
      <PopoverContent
        side='bottom'
        align='start'
        sideOffset={6}
        className='w-72 p-3 font-["Inter",system-ui,sans-serif]'
      >
        <div className='flex flex-col gap-3'>
          <div className='flex flex-col gap-1'>
            <div className='text-[10px] font-medium uppercase tracking-wider text-text-subtle'>
              {scopeLabel(scope)}
            </div>
            <div className='inline-flex h-[24px] w-fit items-center rounded-sm bg-[#B3C2EF] px-2 text-[11px] font-semibold leading-none text-[#1A2D7A]'>
              <span className='text-cobalt-strong'>@</span>
              <span>{alias}</span>
            </div>
          </div>
          <div className='flex flex-col gap-1'>
            <div className='text-[10px] font-medium uppercase tracking-wider text-text-subtle'>
              Canonical ref
            </div>
            <code className='block break-all rounded-sm bg-surface-subtle px-2 py-1 font-mono text-[11px] text-text-secondary'>
              {ref || '—'}
            </code>
          </div>
          {!readOnly && (
            <div className='flex justify-end pt-1'>
              <Button
                type='button'
                size='sm'
                variant='ghost'
                className='gap-1.5 text-status-red-emphasis hover:bg-red-50 hover:text-status-red-emphasis'
                onClick={handleDelete}
              >
                <Trash2 className='h-3.5 w-3.5' />
                Remove
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
